/**
 * PoleReserveDialog — návin (rezerva kábla) na jednom stĺpe.
 *
 * Pridané 2026-09-30. Zadáva sa na stĺpe, lebo v teréne stojíš pri stĺpe
 * a nie „na konci kábla #1499". Ukladá sa ale po kábloch, lebo útlm sa
 * počíta po vlákne v konkrétnom kábli.
 *
 * ZÁMERNE SA NIČ NEDELÍ. Zostup z vrchu stĺpa dolu do krabice je rovnako
 * dlhý bez ohľadu na to, koľko káblov na stĺpe je. Pri krabici je kábel
 * prestrihnutý, takže dolu musia zísť oba a každý má vlastný zostup; pri
 * odbočke s tromi káblami sú tri zostupy. Políčko „vyplniť všetkým" preto
 * rovnakú hodnotu ZAPÍŠE každému káblu, nerozdelí ju medzi ne.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { polesAPI } from '../../services/api';
import { getErrorMessage } from '../../utils/errorMessages';
import './Dialog.css';

interface KabelNavin {
  cable_id: number;
  popis: string;
  cable_type: string;
  length_meters: number;
  je_koniec: boolean;
  meters: number;
}

interface PoleReserveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  poleId: number | null;
  /** Zavolá sa po úspešnom uložení — mapa si má znovu natiahnuť káble */
  onSaved?: () => void;
}

export const PoleReserveDialog: React.FC<PoleReserveDialogProps> = ({
  isOpen,
  onClose,
  projectId,
  poleId,
  onSaved,
}) => {
  const [nazov, setNazov] = useState('');
  const [maKazetu, setMaKazetu] = useState(false);
  const [kable, setKable] = useState<KabelNavin[]>([]);
  const [vsetkym, setVsetkym] = useState<string>('');
  const [nacitavam, setNacitavam] = useState(false);
  const [ukladam, setUkladam] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);

  const nacitaj = useCallback(async () => {
    if (!poleId) return;
    setNacitavam(true);
    setChyba(null);
    try {
      const r: any = await polesAPI.getReserves(projectId, poleId);
      setNazov(r.data.pole_name);
      setMaKazetu(!!r.data.ma_kazetu);
      setKable(r.data.cables || []);
    } catch (e: any) {
      setChyba(getErrorMessage(e, 'Nepodarilo sa načítať káble na tomto stĺpe.'));
    } finally {
      setNacitavam(false);
    }
  }, [projectId, poleId]);

  useEffect(() => {
    if (isOpen && poleId) {
      setVsetkym('');
      nacitaj();
    }
  }, [isOpen, poleId, nacitaj]);

  if (!isOpen || !poleId) return null;

  const suma = kable.reduce((s, k) => s + (Number(k.meters) || 0), 0);

  const nastavVsetkym = (hodnota: string) => {
    setVsetkym(hodnota);
    const n = parseFloat(hodnota);
    if (Number.isNaN(n)) return;
    setKable((z) => z.map((k) => ({ ...k, meters: Math.max(0, n) })));
  };

  const uloz = async () => {
    setUkladam(true);
    setChyba(null);
    try {
      await polesAPI.saveReserves(projectId, poleId, {
        items: kable.map((k) => ({ cable_id: k.cable_id, meters: Number(k.meters) || 0 })),
      });
      if (onSaved) onSaved();
      onClose();
    } catch (e: any) {
      setChyba(getErrorMessage(e, 'Návin sa nepodarilo uložiť.'));
    } finally {
      setUkladam(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>🧵 Návin na stĺpe {nazov}</h2>
          <button className="dialog-close" onClick={onClose}>✕</button>
        </div>

        <div className="dialog-content">
          {chyba && <div className="dialog-error">{chyba}</div>}

          <p style={{ color: '#999', fontSize: '13px', lineHeight: 1.6, marginTop: 0 }}>
            {maKazetu
              ? 'Na tomto stĺpe je krabica s kazetou — kábel je prestrihnutý, takže dolu do krabice musí zísť každý kábel zvlášť a každý má vlastný zostup.'
              : 'Kábel je na stĺp len upnutý a prechádza neprerušený — návin je stočená rezerva.'}
            {' '}Hodnoty sú <b>za každý kábel zvlášť</b>; appka nič nedelí.
          </p>

          {nacitavam ? (
            <p style={{ color: '#999' }}>Načítavam…</p>
          ) : kable.length === 0 ? (
            <p style={{ color: '#e0a800', fontSize: '14px' }}>
              Cez tento stĺp nevedie žiadny kábel, takže návin tu nemá čo držať.
            </p>
          ) : (
            <>
              <div className="form-group">
                <label>Vyplniť všetkým rovnako (metre)</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={vsetkym}
                  placeholder="napr. 8"
                  onChange={(e) => nastavVsetkym(e.target.value)}
                />
                <small className="form-hint">
                  Zapíše rovnakú hodnotu každému káblu nižšie — <b>nerozdelí</b> ju medzi ne.
                  Jednotlivé hodnoty potom môžeš prepísať.
                </small>
              </div>

              <div className="form-group">
                <label>Káble idúce cez tento stĺp</label>
                {kable.map((k, i) => (
                  <div
                    key={k.cable_id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #333',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px' }}>{k.popis}</div>
                      <small style={{ color: '#999' }}>
                        #{k.cable_id} · {k.cable_type} · úsek {k.length_meters.toFixed(1)} m ·{' '}
                        {k.je_koniec ? 'končí tu' : 'prechádza cezeň'}
                      </small>
                    </div>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={k.meters}
                      style={{ width: '90px', flexShrink: 0 }}
                      onChange={(e) => {
                        const n = Math.max(0, parseFloat(e.target.value) || 0);
                        setKable((z) => z.map((x) =>
                          x.cable_id === k.cable_id ? { ...x, meters: n } : x));
                      }}
                    />
                    <span style={{ color: '#999', fontSize: '13px', width: '18px' }}>m</span>
                  </div>
                ))}
                <small className="form-hint">
                  Spolu na stĺpe: <b>{suma.toFixed(1)} m</b>
                  {kable.length > 1 && ` (${kable.length} zostupy)`}. Nula znamená, že tu návin nie je.
                </small>
              </div>
            </>
          )}
        </div>

        <div className="dialog-actions">
          <button className="btn-secondary" onClick={onClose}>Zrušiť</button>
          <button
            className="btn-primary"
            disabled={ukladam || nacitavam || kable.length === 0}
            onClick={uloz}
          >
            {ukladam ? 'Ukladám…' : '💾 Uložiť'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PoleReserveDialog;
